import React, { useState, useEffect } from 'react';
import { Redirect, Route } from 'react-router-dom';
import { IonApp, IonRouterOutlet, IonSpinner, setupIonicReact } from '@ionic/react';
import { Capacitor } from '@capacitor/core';
import { IonReactRouter } from '@ionic/react-router';
import Home from './pages/about-app/Home';
import About from './pages/about-app/About';
import Login from './pages/Login';
import Main from './pages/Main';
import Inspecting from './pages/Inspecting';
import Planning from './pages/Planning';
import Reporting from './pages/Reporting';
import CorrectiveActions from './pages/CorrectiveActions';
import OfflineOperations from './pages/OfflineOperations';
import UserPreferences from './pages/admin/UserPreferences';
import ModuleSettings from './pages/admin/ModuleSettings';
import UserManagement from './pages/admin/UserManagement';
import EnterpriseManagement from './pages/admin/EnterpriseManagement';
import SelectTenant from './pages/SelectTenant';
import { auth } from './services/UserAuthService';
import { tenantService } from './services/TenantService';
import { OfflineProvider } from './contexts/OfflineContext';
import { PageTitleProvider } from './contexts/PageTitleContext';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Theme variables */
import './theme/variables.css';

setupIonicReact();

// Protected Route component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isLoggedIn = auth.loggedIn();
  
  if (!isLoggedIn) {
    return <Redirect to="/login" />;
  }
  
  return <>{children}</>;
};

const DefaultRedirect: React.FC = () => {
  if (Capacitor.isNativePlatform() && !tenantService.hasTenant()) {
    return <Redirect to="/select-tenant" />;
  }
  return <Redirect to={auth.loggedIn() ? '/main' : '/login'} />;
};

const App: React.FC = () => {
  const [tenantReady, setTenantReady] = useState(false);

  useEffect(() => {
    tenantService.initialize().then(() => setTenantReady(true));
  }, []);

  if (!tenantReady) {
    return (
      <IonApp>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <IonSpinner name="crescent" />
        </div>
      </IonApp>
    );
  }

  return (
  <OfflineProvider>
    <IonApp>
      <PageTitleProvider>
        <IonReactRouter>
        <IonRouterOutlet>
          {/* Tenant Selection - Mobile Only */}
          <Route exact path="/select-tenant">
            <SelectTenant />
          </Route>

          {/* Login Route - Public */}
          <Route exact path="/login">
            <Login />
          </Route>
          
          {/* Protected Routes */}
          <Route exact path="/main">
            <ProtectedRoute>
              <Main />
            </ProtectedRoute>
          </Route>
          
          <Route exact path="/inspecting">
            <ProtectedRoute>
              <Inspecting />
            </ProtectedRoute>
          </Route>
          
          <Route exact path="/planning">
            <ProtectedRoute>
              <Planning />
            </ProtectedRoute>
          </Route>
          
          <Route exact path="/corrective-actions">
            <ProtectedRoute>
              <CorrectiveActions />
            </ProtectedRoute>
          </Route>
          
          <Route exact path="/reporting">
            <ProtectedRoute>
              <Reporting />
            </ProtectedRoute>
          </Route>

          <Route exact path="/offline-operations">
            <ProtectedRoute>
              <OfflineOperations />
            </ProtectedRoute>
          </Route>

          <Route exact path="/user-preferences">
            <ProtectedRoute>
              <UserPreferences />
            </ProtectedRoute>
          </Route>

          <Route exact path="/module-settings">
            <ProtectedRoute>
              <ModuleSettings />
            </ProtectedRoute>
          </Route>

          <Route exact path="/user-management">
            <ProtectedRoute>
              <UserManagement />
            </ProtectedRoute>
          </Route>

          <Route exact path="/enterprise-management">
            <ProtectedRoute>
              <EnterpriseManagement />
            </ProtectedRoute>
          </Route>
          
          {/* Legacy routes - keeping for compatibility */}
          <Route exact path="/home">
            <Home />
          </Route>
          <Route exact path="/about">
            <About />
          </Route>
          
          {/* Default redirect */}
          <Route exact path="/">
            <DefaultRedirect />
          </Route>
          
          {/* Catch-all redirect */}
          <Route>
            <DefaultRedirect />
          </Route>
        </IonRouterOutlet>
      </IonReactRouter>
      </PageTitleProvider>
    </IonApp>
  </OfflineProvider>
  );
};

export default App;